import React, { useRef, useState, useEffect, useCallback } from "react";
import images from "../../../assets/images";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search, CheckCircle, Plus } from "lucide-react";
import { toast } from "react-toastify";
import {
  useEquipmentManagementStore,
  usePremisesStore,
  useRegionStore,
  useOrgUsersStore,
  useFireSafetyAudits,
} from "../../../store/store";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import CreateTaskModal from "./components/CreateTaskModal";
import { EquipmentInventory, MaintenanceTask } from "../../../types/api";
import { getAllEquipmentManagementData } from "../../../service/api";
import { useMaintenanceTypeListStore } from "../../../store/masterDataStore";
import { EQUIPMENT_MANAGEMENT_MAINTENANCE_SCHEDULING_AND_TRACKING } from "../../../constants/modules";
import { usePermissions } from "../../../hooks/usePermission";
import { useLocation, useNavigate } from "react-router-dom";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const MaintenanceSchedulingAndTracking: React.FC = () => {
  const { canCreate, canRead } = usePermissions();
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isSelectedTask, setIsSelectedTask] = useState<MaintenanceTask | null>(
    null,
  );
  const [mode, setMode] = useState<"create" | "view" | "update" | "complete">(
    "create",
  );
  const [rowData, setRowData] = useState<MaintenanceTask[]>([]);

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [equipmentData, setEquipmentData] = useState<EquipmentInventory[]>([]);
  const [stats, setStats] = useState({
    totalTasks: 0,
    scheduled: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
  });
  const navigate = useNavigate();
  const location = useLocation();

  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { fetchAllMaintenanceSchedule, maintenanceTasks } =
    useEquipmentManagementStore();
  const gridRef = useRef<AgGridReact>(null);
  const { fetchPremises } = usePremisesStore();
  const { fetchRegions } = useRegionStore();
  const { maintenanceTypeList, fetchAllMaintenanceTypeList } =
    useMaintenanceTypeListStore();
  // Initialize premises and regions data
  useEffect(() => {
    fetchPremises();
    fetchRegions();

    fetchAllMaintenanceSchedule();
    fetchOrgUsers();
    fetchAllMaintenanceTypeList();
  }, [
    fetchPremises,
    fetchRegions,
    fetchAllMaintenanceSchedule,
    fetchOrgUsers,

    fetchAllMaintenanceTypeList,
  ]);

  const fetchEquipmentData = useCallback(async () => {
    try {
      const response = await getAllEquipmentManagementData();

      if (response.status == 200) {
        setEquipmentData(response.data?.results as EquipmentInventory[]);
      }
    } catch (error: any) {
      toast.error("Failed to fetch equipment data");
      console.error("Error fetching equipment data:", error);
    }
  }, [getAllEquipmentManagementData]);

  useEffect(() => {
    fetchEquipmentData();
  }, [fetchEquipmentData]);

  const fetchMaintenanceTasks = useCallback(async () => {
    try {
      setLoading(true);
      // Use the actual maintenance tasks from the store
      const tasks = maintenanceTasks || [];
      setRowData(tasks);

      // Calculate stats
      const stats = {
        totalTasks: tasks.length,
        scheduled: tasks.filter((task) => task.status === "Scheduled").length,
        inProgress: tasks.filter((task) => task.status === "In Progress")
          .length,
        completed: tasks.filter((task) => task.status === "Completed").length,
        overdue: tasks.filter((task) => task.status === "Overdue").length,
      };
      setStats(stats);
    } catch (error) {
      console.error("Failed to fetch maintenance tasks:", error);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, [maintenanceTasks]);

  useEffect(() => {
    fetchMaintenanceTasks();
  }, [fetchMaintenanceTasks]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = rowData.filter((task) => {
    const matchesSearch =
      task.taskId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.equipmentName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPremises = selectedPremises
      ? task.hospitalName === selectedPremises
      : true;
    const matchesRegion = selectedRegion
      ? task.region === selectedRegion
      : true;
    return matchesSearch && matchesPremises && matchesRegion;
  });
  const handleViewTask = (data: MaintenanceTask) => {
    setIsSelectedTask(data);
    setMode("view");
    setIsTaskModalOpen(true);
  };

  const handleEditTask = (data: MaintenanceTask) => {
    setIsSelectedTask(data);
    setMode("update");
    setIsTaskModalOpen(true);
  };

  const handleCreateTask = () => {
    setIsSelectedTask(null);
    setMode("create");
    setIsTaskModalOpen(true);
  };

  const handleTaskComplete = () => {
    setIsTaskModalOpen(false);
    setIsSelectedTask(null);
    setMode("create");
    fetchAllMaintenanceSchedule();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const extractId = (value: any): string =>
    typeof value === "string" ? value : value?.$oid || "";

  const getUserName = (assignedToId: string): string => {
    if (!assignedToId || !orgUsersData?.length) return assignedToId || "N/A";

    const user = orgUsersData.find(
      (u: any) => extractId(u._id) === assignedToId,
    );
    if (!user) return assignedToId;

    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || user.name || user.email || assignedToId;
  };

  const getEquipmentName = (id: string): string => {
    if (!id || !equipmentData?.length) return id || "N/A";

    const equipment = equipmentData.find((e: any) => extractId(e._id) === id);
    if (!equipment) return id;
    return equipment.equipmentName || equipment.equipment || id;
  };

  const getMaintenanceType = (id: string): string => {
    if (!id || !maintenanceTypeList?.length) return id || "N/A";

    const maintenanceType = maintenanceTypeList.find(
      (e: any) => extractId(e._id) === id,
    );
    if (!maintenanceType) return id;
    return maintenanceType.maintenanceTypeName || id || "N/A";
  };

  const columnDefs: ColDef<MaintenanceTask>[] = [
    {
      headerName: "Task ID",
      field: "taskId",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 5px" },
    },
    {
      headerName: "Equipment",
      field: "equipmentName",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        return getEquipmentName(params.value);
      },
    },
    {
      headerName: "Type",
      field: "maintenanceType",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        return getMaintenanceType(params.value);
      },
    },
    {
      headerName: "Scheduled Date",
      field: "scheduledDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Assigned To",
      field: "assignedTo",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        return getUserName(params.value);
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        let color = "black";
        switch (params.value) {
          case "Completed":
            color = "#4CAF50";
            break;
          case "In Progress":
            color = "#2196F3";
            break;
          case "Overdue":
            color = "#F44336";
            break;
          case "Scheduled":
            color = "#FF9800";
            break;
        }
        return {
          color,
          fontWeight: "bold",
          padding: "10px 0 0 0",
        };
      },
    },
    {
      headerName: "Action",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        const task = params.data;
        const canComplete =
          task.status === "Scheduled" || task.status === "In Progress";
        const canEdit = task.status !== "Completed";

        return (
          <div className="flex items-center gap-2">
            <button onClick={() => handleViewTask(task)} title="View Task">
              <img className="iconTd" src={images.viewIcon} alt="View" />
            </button>

            {canEdit && (
              <button onClick={() => handleEditTask(task)} title="Edit Task">
                <img className="iconTd" src={images.editIcon} alt="Edit" />
              </button>
            )}

            <button
              onClick={() => toast.info("Download functionality coming soon")}
              title="Download Report"
            >
              <img
                className="iconTd"
                src={images.downloadIcon}
                alt="Download"
              />
            </button>
          </div>
        );
      },
    },
  ];

  const handelNavigateToCheckList = () => {
    navigate(
      "/equipment-management/maintenance-scheduling-and-tracking/check-list",
    );
  };

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6">
        Maintenance Scheduling & Tracking
      </h1>

      <span className="flex justify-between gap-4">
        <span className="flex items-center gap-4">
          <Dropdown
            selectedValue={selectedRegion}
            onChange={setSelectedRegion}
            type="region"
          />
          <Dropdown
            selectedValue={selectedPremises}
            onChange={setSelectedPremises}
            type="premises"
            selectedRegion={selectedRegion}
          />
        </span>
        {canCreate(
          EQUIPMENT_MANAGEMENT_MAINTENANCE_SCHEDULING_AND_TRACKING,
        ) && (
            <span className="flex items-center">
              <button
                className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
                onClick={handelNavigateToCheckList}
              >
                <img src={images.tasksIcon} alt="" width={25} />
                CHECK LIST
              </button>
            </span>
          )}

        {canCreate(
          EQUIPMENT_MANAGEMENT_MAINTENANCE_SCHEDULING_AND_TRACKING,
        ) && (
            <span className="flex items-center">
              <button
                className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
                onClick={handleCreateTask}
              >
                <Plus className="w-5 h-5" />
                CREATE TASK
              </button>
            </span>
          )}
      </span>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 my-8">
        <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">
              Total Tasks
            </h3>
            <p className="text-3xl font-bold text-gray-800">
              {stats.totalTasks}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">Scheduled</h3>
            <p className="text-3xl font-bold text-[#FF9800]">
              {stats.scheduled}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">
              In Progress
            </h3>
            <p className="text-3xl font-bold text-[#2196F3]">
              {stats.inProgress}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">Completed</h3>
            <p className="text-3xl font-bold text-[#4CAF50]">
              {stats.completed}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">Overdue</h3>
            <p className="text-3xl font-bold text-[#F44336]">{stats.overdue}</p>
          </div>
        </div>
      </div>

      {/* Search and Table */}
      <div className="bg-[#ffffff] w-[100%]">
        <div className="px-3 py-3">
          <div className="relative flex justify-between items-center">
            <div className="flex w-10/12 justify-start items-center">
              <Search className="absolute left-3 text-gray-500" />
              <input
                type="text"
                placeholder="Search by Task ID or Equipment"
                className="border-2 border-[#4f5762] mr-5 w-[300px] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>

        <div className="ag-theme-alpine mt-4">
          {loading ? (
            <div className="text-center py-4">Loading maintenance tasks...</div>
          ) : (
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              rowHeight={50}
              headerHeight={65}
              paginationPageSize={10}
              pagination={true}
              domLayout="autoHeight"
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              getRowStyle={(params) => {
                const isOverdue = params.data.status === "Overdue";
                return {
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginBottom: "8px",
                  borderRadius: "4px",
                  backgroundColor: isOverdue
                    ? "#fff5f5"
                    : params.data.status === "Completed"
                      ? "#f6fff5"
                      : "white",
                  borderLeft: isOverdue
                    ? "4px solid #F44336"
                    : params.data.status === "Completed"
                      ? "4px solid #4CAF50"
                      : "none",
                };
              }}
            />
          )}
        </div>
      </div>

      {/* Task Modal */}
      {isTaskModalOpen && (
        <CreateTaskModal
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          task={isSelectedTask as MaintenanceTask}
          mode={mode}
          onTaskComplete={handleTaskComplete}
        />
      )}
    </div>
  );
};

export default MaintenanceSchedulingAndTracking;
